/**
 * demo-data.ts —— 演示数据的构造器
 *
 * ⚠️ 这不是 mock。
 *
 * 无 mock 原则（D5）禁止的是「假装能用的空壳」和「误导测试的假数据」。
 * 这个模块与 D5 不冲突，理由是：
 *   - 它**只在用户主动点击「载入演示数据」时**才执行，绝不自动写入（D2）
 *   - 它写入的是**真实数据**：真的进 IndexedDB、真的参与排序检索、
 *     真的能被 AI 读写、真的能发消息
 *   - 它的用途是快速构造测试场景，而不是演一出动画
 *
 * 数据内容刻意保持朴素（普通姓名、普通对话），不使用「测试用户1」这类
 * 明显的占位符 —— 那样会让测试者一眼看出是造的数据，干扰真实体感。
 */

import { contactsRepo } from './repo.contacts';
import { sessionsRepo } from './repo.sessions';
import { messagesRepo } from './repo.messages';
import { eventBus } from '@/core/event-bus';

interface DemoContact {
  name: string;
  remark?: string;
  signature?: string;
  /** 与「我」的历史对话，按时间顺序 */
  messages: { from: 'me' | 'them'; text: string }[];
}

const DEMO: DemoContact[] = [
  {
    name: '王二',
    signature: '在做一个不太靠谱的项目',
    messages: [
      { from: 'them', text: '在吗' },
      { from: 'me', text: '在的，怎么了' },
      { from: 'them', text: '那个方案我看了，感觉第三部分要再改改' },
      { from: 'me', text: '好，我今晚改一下发你' },
    ],
  },
  {
    name: '李静',
    remark: '产品-李静',
    signature: '今天也要好好写文档',
    messages: [
      { from: 'me', text: '需求评审定在周四下午可以吗' },
      { from: 'them', text: '可以，我拉个会议' },
      { from: 'them', text: '记得把原型图带上' },
    ],
  },
  {
    name: '陈默',
    signature: '少说话，多做事',
    messages: [
      { from: 'them', text: '服务器那台我先重启了' },
      { from: 'me', text: '收到，辛苦了' },
    ],
  },
  {
    name: '赵敏',
    signature: '',
    messages: [{ from: 'them', text: '周末有空一起打球吗' }],
  },
  {
    name: '周源',
    signature: '出差中，回复慢',
    messages: [
      { from: 'me', text: '合同寄出了吗' },
      { from: 'them', text: '寄了，顺丰，明天到' },
    ],
  },
  {
    name: '孙小雨',
    signature: '咖啡续命',
    messages: [{ from: 'them', text: '楼下新开了家咖啡店，去试试？' }],
  },
  {
    name: '吴思远',
    signature: '',
    messages: [],
  },
  {
    name: '郑一鸣',
    signature: '别催，在改了',
    messages: [
      { from: 'me', text: '那个 bug 定位到了吗' },
      { from: 'them', text: '定位到了，是空指针，今天就能修好' },
      { from: 'me', text: '好的' },
    ],
  },
];

/** 每条历史消息之间的时间间隔（毫秒），制造合理的时间分布 */
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

/**
 * 载入演示数据。返回写入的联系人与会话数量。
 *
 * 时间分布刻意做出层次：越靠后的联系人，最后互动时间越接近当下。
 * 这样会话列表倒序排列时，能看到「今天 / 昨天 / 几天前」自然分层，
 * 而不是所有会话挤在同一时刻。演示数据也要经得起看。
 */
export async function loadDemoData(): Promise<{ contacts: number; sessions: number }> {
  let sessionCount = 0;

  // 从 6 天前开始，逐个联系人向前推 —— 最后一个联系人的会话时间最接近现在
  const spanDays = 6;
  const stepMs = (spanDays * 24 * HOUR) / Math.max(DEMO.length, 1);
  let base = Date.now() - spanDays * 24 * HOUR;

  for (const item of DEMO) {
    const contact = await contactsRepo.create({
      name: item.name,
      remark: item.remark,
      signature: item.signature,
    });

    base += stepMs;

    if (item.messages.length === 0) continue;

    const session = await sessionsRepo.ensureForContact(contact.id);
    sessionCount += 1;

    // 该联系人这段对话内部，每条消息间隔几分钟
    let cursor = base;
    for (const msg of item.messages) {
      cursor += 3 * MINUTE + Math.floor(Math.random() * 12) * MINUTE;
      await messagesRepo.append({
        sessionId: session.id,
        sender: msg.from === 'me' ? 'me' : contact.id,
        text: msg.text,
        createdAt: cursor,
      });
    }

    // 把会话时间戳对齐到最后一条消息，让列表排序正确
    const last = item.messages[item.messages.length - 1];
    await sessionsRepo.touch(session.id, last.text.slice(0, 60), cursor);

    // 对方最后说话且「我」没回的，留一条未读，让首页看起来有生气
    if (last.from === 'them') {
      await sessionsRepo.incrementUnread(session.id);
    }
  }

  eventBus.emit('contact:list-changed', {});
  eventBus.emit('session:list-changed', {});
  eventBus.emit('data:demo-loaded', { contacts: DEMO.length, sessions: sessionCount });

  return { contacts: DEMO.length, sessions: sessionCount };
}
